'use client';

import type { ComponentProps, PropsWithChildren } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { BoxProps } from 'components/Box/Box';
import { Box } from 'components/Box/Box';
import type { Bounds } from 'hooks/useFlip/types';
import type { CustomStyleOverrides } from 'utils/types';
import {
  mergeRefs,
  useEnterAnimation,
  useExitAnimation,
} from '@react-aria/utils';
import type { ThemeRadii } from '@frameio/vapor-theme';
import { SharedHoverProvider } from './DefaultSharedHover';
import styles from './SharedHover.module.css';

export type SharedHoverProps = PropsWithChildren<{
  mouseLeaveTimeout?: number;
  hoverRadius?: ThemeRadii;
  color?: HoverIndicatorColor;
  transitionDuration?: number;
  Provider?: typeof SharedHoverProvider;
}> &
  CustomStyleOverrides &
  Omit<BoxProps, 'position' | 'color'>;

type State = {
  target: Bounds | null;
  bounds: Bounds | null;
  lockedBounds: Bounds | null;
};

type Action =
  | { type: 'ENTER_TARGET'; payload: { bounds: Bounds; target: Bounds } }
  | { type: 'EXIT_TARGET' }
  | { type: 'LOCK_TARGET'; payload: { bounds: Bounds } }
  | { type: 'UNLOCK_TARGET'; payload: { bounds: Bounds } };

const defaultState: State = {
  target: null,
  bounds: null,
  lockedBounds: null,
};

function reducer(state: State = defaultState, action: Action) {
  switch (action.type) {
    case 'ENTER_TARGET': {
      return {
        ...state,
        bounds: action.payload.bounds,
        target: action.payload.target,
      };
    }
    case 'EXIT_TARGET': {
      return {
        ...state,
        target: null,
      };
    }
    case 'LOCK_TARGET': {
      return {
        ...state,
        lockedBounds: action.payload.bounds,
      };
    }
    case 'UNLOCK_TARGET': {
      if (state.lockedBounds === action.payload.bounds) {
        return {
          ...state,
          lockedBounds: null,
        };
      }
      return state;
    }
    default:
      throw new Error(
        `Unexpected action. You aren't using typescript, are you?`
      );
  }
}

export function SharedHover({
  Provider = SharedHoverProvider,
  children,
  mouseLeaveTimeout = 20,
  transitionDuration = 100,
  UNSAFE_CSS,
  UNSAFE_STYLE,
  className,
  style,
  color = 'secondary',
  hoverRadius = 6,
  ...other
}: SharedHoverProps) {
  const [{ target, bounds, lockedBounds }, dispatch] = useReducer(
    reducer,
    defaultState
  );

  const timerRef = useRef<number | null>(null);
  const boundsRef = useRef<HTMLDivElement>(null);

  const lastDefinedTargetRef = useRef<Bounds | null>(null);

  useEffect(() => {
    // Store the last remaining defined value. We need
    // to hold this to ensure when we unmount our slider
    // we keep the hover indicator in the correct position.
    if (target !== null && !lockedBounds) {
      lastDefinedTargetRef.current = target;
    }

    if (lockedBounds) {
      lastDefinedTargetRef.current = lockedBounds;
    }
  }, [lockedBounds, target]);

  /**
   * Measure our bounds each time a hover occurs.
   *
   * An alternative would be to use
   * a resize observer, but I think this is actually more efficient
   * and less likely to cause issues. It is dependent upon the container
   * _not_ changing size during hover, though. But we shouldn't be using
   * this in highly dynamic cases anyway.
   */

  const onEnterTarget = useCallback((target: Bounds) => {
    const bounds = boundsRef.current!.getBoundingClientRect();
    dispatch({ type: 'ENTER_TARGET', payload: { bounds, target } });
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }
  }, []);

  const onLeaveTarget = useCallback(() => {
    timerRef.current = window.setTimeout(() => {
      dispatch({ type: 'EXIT_TARGET' });
    }, mouseLeaveTimeout);
  }, [mouseLeaveTimeout]);

  const onLockTarget = useCallback((bounds: Bounds) => {
    dispatch({ type: 'LOCK_TARGET', payload: { bounds } });
  }, []);

  const onUnlockTarget = useCallback((bounds: Bounds) => {
    dispatch({ type: 'UNLOCK_TARGET', payload: { bounds } });
  }, []);

  const context = useMemo(() => {
    return {
      onEnterTarget,
      onLeaveTarget,
      onLockTarget,
      onUnlockTarget,
    };
  }, [onEnterTarget, onLeaveTarget, onLockTarget, onUnlockTarget]);

  const showHoverIndicator = target !== null || lockedBounds;

  // When unmounting, we need to position it in its last viable state.
  let actualTarget = showHoverIndicator ? target : lastDefinedTargetRef.current;

  if (lockedBounds) {
    actualTarget = lockedBounds;
  }

  /**
   * Determine the position of the hover indicator given the target position,
   * and the container position.
   */

  const hoverStyle = useMemo(() => {
    if (!actualTarget || !bounds) {
      return null;
    }

    const y = actualTarget.top - bounds.top;
    const x = actualTarget.left - bounds.left;
    const w = actualTarget.width;
    const h = actualTarget.height;

    return {
      borderRadius: `${hoverRadius}px`,
      width: `${w}px`,
      height: `${h}px`,
      transform: `translate(${x}px, ${y}px)`,
    };
  }, [actualTarget, bounds, hoverRadius]);

  const nodeRef = useRef(null);

  const isExiting = useExitAnimation(nodeRef, Boolean(showHoverIndicator));

  return (
    <Provider value={context}>
      <Box
        ref={boundsRef as any}
        className={className}
        css={UNSAFE_CSS}
        position="relative"
        style={{ ...style, ...UNSAFE_STYLE }}
        {...other}
      >
        {showHoverIndicator || isExiting ? (
          <AnimatedHoverIndicator
            ref={nodeRef}
            borderRadius={hoverRadius}
            color={color}
            isExiting={isExiting}
            style={hoverStyle || undefined}
            transitionDuration={lockedBounds ? 0 : transitionDuration}
          />
        ) : null}

        {children}
      </Box>
    </Provider>
  );
}

export const AnimatedHoverIndicator = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof HoverIndicator> & {
    isExiting: boolean;
  }
>(function AnimatedHoverIndicator({ isExiting, ...props }, ref) {
  const localRef = useRef(null);
  const isEntering = useEnterAnimation(localRef);
  return (
    <HoverIndicator
      ref={mergeRefs(ref, localRef)}
      data-entering={isEntering || undefined}
      data-exiting={isExiting || undefined}
      {...props}
    />
  );
});

type HoverIndicatorColor = 'neutral' | 'secondary';

type HoverIndicatorProps = {
  borderRadius: ThemeRadii;
  transitionDuration: number;
  color: HoverIndicatorColor;
} & React.HTMLAttributes<HTMLDivElement>;

const HoverIndicator = forwardRef<HTMLDivElement, HoverIndicatorProps>(
  function HoverIndicator(
    { borderRadius, transitionDuration, color, style, ...props },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={styles.hoverIndicator}
        data-color={color}
        style={
          {
            '--vapor-shared-hover-border-radius': `var(--radii-${borderRadius})`,
            '--vapor-shared-hover-transition-duration': `${transitionDuration}ms`,
            ...style,
          } as React.CSSProperties
        }
        {...props}
      />
    );
  }
);
